import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { useCallback } from 'react'
import { getSearchParams } from '@tmap-web-lib/utils'
import Url from 'url-parse'
import isEmpty from 'lodash-es/isEmpty'
import { PageIdSearchParams } from '@/providers/page-id-context'
import { RoutePath } from '@/routes'
import { useErrorLogs } from '@/hooks'

function useOpenServiceUrl() {
  const errorLogs = useErrorLogs()

  return useCallback((srcUrl: string, openAnotherService?: string) => {
    if (!srcUrl) {
      return
    }

    const env = window.location.href.split('.')[0]
    let path

    // 메인 페이지에서 컨텐츠 배너 클릭 접근
    if (srcUrl.startsWith('tmap')) {
      const urlObject = new Url(srcUrl)
      const params = getSearchParams(urlObject.query || '')
      const searchParams = getSearchParams<PageIdSearchParams>()
      const { ...paramsExceptPageid } = searchParams
      const scrollToObj = { scroll_to: params.extra && JSON.parse(params.extra).scroll_to }
      const seqObj = { seq: params.extra && JSON.parse(params.extra).seq }
      const dateObj = { date: params.extra && JSON.parse(params.extra).date }
      const moveToObj = { move_to: params.extra && JSON.parse(params.extra).move_to }
      const switchObj = { switch: params.extra && JSON.parse(params.extra).switch }
      const actionObj = { action: params.extra && JSON.parse(params.extra).action }
      const missionInventoryCodeObj = { missionInventoryCode: params.extra && JSON.parse(params.extra).missionInventoryCode }
      const missionProductIdObj = { missionProductId: params.extra && JSON.parse(params.extra).missionProductId }
      const pointRewardStartDateObj = { pointRewardStartDate: params.extra && JSON.parse(params.extra).pointRewardStartDate }
      const pointRewardEndDateObj = { pointRewardEndDate: params.extra && JSON.parse(params.extra).pointRewardEndDate }

      const defaultParams = `&extra=${encodeURIComponent(JSON.stringify({
        is_first_history: true,
        logbox: { origin: 'tab', type: 'tab' },
        ...scrollToObj,
        ...seqObj,
        ...dateObj,
        ...moveToObj,
        ...switchObj,
        ...actionObj,
        ...missionInventoryCodeObj,
        ...missionProductIdObj,
        ...pointRewardStartDateObj,
        ...pointRewardEndDateObj,
      }))}`
      const extraParams = `extra=${encodeURIComponent(JSON.stringify({
        ...JSON.parse(
          paramsExceptPageid.extra?.startsWith('%')
            ? decodeURIComponent(paramsExceptPageid.extra)
            : paramsExceptPageid.extra || '{}',
        ),
        ...scrollToObj,
        ...seqObj,
        ...dateObj,
        ...moveToObj,
        ...switchObj,
        ...actionObj,
        ...missionInventoryCodeObj,
        ...missionProductIdObj,
        ...pointRewardStartDateObj,
        ...pointRewardEndDateObj,
      }))}`
      const inDrivingLifeSearchParams = isEmpty(searchParams.extra) ? defaultParams : extraParams

      switch (params.pageid) {
        case 'benefit_home':
          path = `${RoutePath.Benefit_home}?${inDrivingLifeSearchParams}`
          break
        case 'benefit_list':
          path = `${RoutePath.Benefit_list}?${inDrivingLifeSearchParams}`
          break
        case 'benefit_detail':
          path = `${RoutePath.Benefit_detail}?${inDrivingLifeSearchParams}`
          break
        case 'benefit_point':
          path = `${RoutePath.Benefit_point}?${inDrivingLifeSearchParams}`
          break
        // 레거시 리디렉션용
        case 'benefit':
          path = `${RoutePath.Benefit_list}?${inDrivingLifeSearchParams}`
          break
        // 레거시 리디렉션용
        case 'benefit_popular':
          path = `${RoutePath.Benefit_home}?${inDrivingLifeSearchParams}`
          break
        case 'driving_analysis':
          path = `${RoutePath.Analysis}?${inDrivingLifeSearchParams}`
          break
        case 'driving_analysis_place':
          path = `${RoutePath.Analysis_place}?${inDrivingLifeSearchParams}`
          break
        case 'vehicle':
          path = `${RoutePath.Vehicle}?${inDrivingLifeSearchParams}`
          break
        case 'vehicle_setting':
          path = `${RoutePath.Vehicle_setting}?${inDrivingLifeSearchParams}`
          break
        case 'vehicle_detail':
          path = `${RoutePath.Vehicle_detail}?${inDrivingLifeSearchParams}`
          break
        case 'unpaid_toll':
          path = `${RoutePath.Vehicle_unpaid_toll}?${inDrivingLifeSearchParams}`
          break
        case 'driving_history':
        case 'trip_history':
          path = `${RoutePath.History}?${inDrivingLifeSearchParams}`
          break
        case 'driving_history_detail':
        case 'trip_history_detail':
          path = `${RoutePath.History_detail}?${inDrivingLifeSearchParams}`
          break
        case 'etc_insurer_contact':
          path = `${RoutePath.Etc_insurer_contact}?${inDrivingLifeSearchParams}`
          break
        case 'etc_emergency_call':
          path = `${RoutePath.Etc_emergency_call}?${inDrivingLifeSearchParams}`
          break
        case 'etc_terms':
          path = `${RoutePath.Etc_terms}?${inDrivingLifeSearchParams}`
          break
        case 'settings':
          path = `${RoutePath.Settings}?${inDrivingLifeSearchParams}`
          break
        case 'settings_otp':
          path = `${RoutePath.Settings_otp}?${inDrivingLifeSearchParams}`
          break
        case 'settings_withdrawal':
          path = `${RoutePath.Settings_withdrawal}?${inDrivingLifeSearchParams}`
          break
        case 'license_penalty':
          path = `${RoutePath.License_penalty}?${inDrivingLifeSearchParams}`
          break
        case 'license_penalty_input':
          path = `${RoutePath.License_penalty_input}?${inDrivingLifeSearchParams}`
          break
        case 'license_penalty_result':
          path = `${RoutePath.License_penalty_result}?${inDrivingLifeSearchParams}`
          break
        case 'insurance_driver':
          path = `${RoutePath.Insurance_driver}?${inDrivingLifeSearchParams}`
          break
        case 'insurance_driver_history':
          path = `${RoutePath.Insurance_driver_history}?${inDrivingLifeSearchParams}`
          break
        case 'insurance_driver_history_detail':
          path = `${RoutePath.Insurance_driver_history_detail}?${inDrivingLifeSearchParams}`
          break
        default:
          path = ''
          break
      }
    } else {
      path = srcUrl
    }

    if (openAnotherService === 'carLife') {
      TmapApp.openServiceByUrl({
        url: `https://m.tmap.co.kr${path}`,
        title: '',
        cacheControl: true,
        portraitonly: true,
      })
    } else if (openAnotherService === 'terms') {
      const href = window.location.href

      if (href.startsWith('https://tscore-dev.')) {
        TmapApp.openServiceByUrl({
          url: `https://dev.tmap.co.kr/tmap2/m/app${path}`, title: '', cacheControl: true, portraitonly: true,
        })
      } else if (href.startsWith('https://tscore-dtg.') || href.startsWith('https://tscore-stg.')) {
        TmapApp.openServiceByUrl({
          url: `https://stg.tmap.co.kr/tmap2/m/app${path}`, title: '', cacheControl: true, portraitonly: true,
        })
      } else {
        TmapApp.openServiceByUrl({
          url: `https://tmap.co.kr/tmap2/m/app${path}`, title: '', cacheControl: true, portraitonly: true,
        })
      }
    } else {
      if (path) {
        TmapApp.openServiceByUrl({
          url: `${env}.tmobiweb.com${path}`, title: '', cacheControl: true, portraitonly: true,
        })
      } else {
        errorLogs({ message: `tscore:error:잘못된 경로로 새 웹뷰 띄움:${srcUrl}` })
        TmapApp.makeToast({ msg: '일시적인 접속 오류로 복구 중입니다.' })
      }
    }
  }, [errorLogs])
}

export { useOpenServiceUrl }
